/**
 * FloatingWhatsApp.tsx
 * --------------------------------------------------------------------------
 * Boton flotante fijo en la esquina inferior derecha que abre wa.me con un
 * mensaje pre-rellenado.
 *
 * Rediseño visual (handoff Ceciluz Arte — 2026-05-25):
 *  Se reemplazó el verde sanación genérico por una pieza alineada con el
 *  universo de marca: gradiente violeta noche, aro dorado externo, ícono
 *  crema/dorado suave, micro-ornamentos místicos (estrella, destello, luna)
 *  y respiración espiritual sutil (escala + glow dorado, 3.2 s ease-in-out).
 *  `prefers-reduced-motion` anula la respiración. Hover/focus/active con
 *  estados accesibles (anillo dorado, elevación, scale 0.97). El tooltip
 *  contextual se renderiza con un sibling controlado por `group-hover`.
 *
 * Fuente del número (sin cambios — funcionalidad preservada):
 *  1. Prop `number` (escape hatch para tests).
 *  2. `site_settings.contact.whatsapp` vía `useSiteSettings()` — fuente
 *     editable por el admin desde el CMS.
 *  3. `import.meta.env.VITE_WHATSAPP_NUMBER` como fallback en cold-start.
 *
 * Si ninguna fuente provee número válido → devuelve `null`. Acepta cualquier
 * formato; internamente se sanea a sólo dígitos.
 *
 * El href, target, rel y aria-label se conservan sin alteración semántica.
 */
import { cn } from '../../lib/cn';
import { useSiteSettings } from '../../features/home/api';
import { WhatsAppLogo } from '../icons/WhatsAppLogo';
import { useFloatingHidden } from './floating-visibility';

// Tipos exportados ----------------------------------------------------------

export interface FloatingWhatsAppProps {
  /**
   * Permite sobreescribir el numero (formato E.164 sin "+"). Si no se
   * pasa, se intenta leer de `site_settings.contact.whatsapp` y, como
   * fallback, de `VITE_WHATSAPP_NUMBER`.
   */
  number?: string;
  /** Mensaje a pre-rellenar (sin codificar). */
  message?: string;
  /** Etiqueta accesible. Default: "Escríbenos por WhatsApp". */
  ariaLabel?: string;
}

// Mensaje por defecto — sin cambios respecto a la versión previa.
const DEFAULT_MESSAGE = 'Hola, me gustaria saber mas';

/**
 * Keyframes locales del botón. Se montan una sola vez vía <style> inline
 * para no contaminar `index.css` con utilidades de un único consumidor.
 * `prefers-reduced-motion` se respeta dentro del propio bloque CSS.
 */
const KEYFRAMES_CSS = `
@keyframes ceciluzWhatsappBreath {
  0%, 100% {
    transform: scale(1);
    box-shadow:
      0 12px 28px rgba(26, 20, 38, 0.30),
      0 0 0 rgba(212, 175, 55, 0);
  }
  50% {
    transform: scale(1.035);
    box-shadow:
      0 16px 34px rgba(26, 20, 38, 0.40),
      0 0 18px rgba(212, 175, 55, 0.28);
  }
}
.ceciluz-wa-btn {
  animation: ceciluzWhatsappBreath 3.2s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .ceciluz-wa-btn { animation: none; }
}
`;

function readWhatsappFromSettings(raw: unknown): string | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const contact = (raw as Record<string, unknown>).contact;
  if (!contact || typeof contact !== 'object') return undefined;
  const value = (contact as Record<string, unknown>).whatsapp;
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function sanitizeNumber(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 8) return null;
  return digits;
}

// Componente ----------------------------------------------------------------

export function FloatingWhatsApp({
  number,
  message = DEFAULT_MESSAGE,
  ariaLabel = 'Escríbenos por WhatsApp',
}: FloatingWhatsAppProps) {
  const siteSettings = useSiteSettings();
  // Una sección (p. ej. el reproductor de la bienal) puede pedir ocultarlo.
  const hidden = useFloatingHidden();

  const fromSettings = readWhatsappFromSettings(siteSettings.data);
  const fromEnv = import.meta.env.VITE_WHATSAPP_NUMBER;
  const candidate = number ?? fromSettings ?? (typeof fromEnv === 'string' ? fromEnv : undefined);

  if (!candidate || typeof candidate !== 'string') return null;
  const digits = sanitizeNumber(candidate);
  if (!digits) return null;

  const href = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;

  return (
    <>
      {/* Keyframes locales — escopa global pero sólo lo usa este componente. */}
      <style dangerouslySetInnerHTML={{ __html: KEYFRAMES_CSS }} />

      {/* Wrapper `group` para coordinar hover entre botón y tooltip. */}
      <div
        aria-hidden={hidden || undefined}
        className={cn(
          'group fixed z-sticky',
          // Bottom-right con separación responsive — desktop más holgado.
          'bottom-4 right-4 md:bottom-7 md:right-7',
          'transition-opacity duration-2 ease-out',
          hidden && 'pointer-events-none opacity-0',
        )}
      >
        {/* Tooltip — sólo visible en hover desktop. Mobile no se muestra
            (no hay cursor). Es 100% decorativo: el aria-label del enlace
            cubre la accesibilidad real. */}
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute right-full top-1/2 mr-3 hidden -translate-y-1/2',
            'whitespace-nowrap rounded-md',
            'border border-gold-500/50 bg-primary-900 px-3 py-1.5',
            'text-caption text-cream-100 shadow-elev2',
            'opacity-0 transition-opacity duration-2 ease-out',
            'md:inline-block md:group-hover:opacity-100 md:group-focus-within:opacity-100',
          )}
        >
          Escríbenos por WhatsApp
        </span>

        {/* Botón principal */}
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={ariaLabel}
          tabIndex={hidden ? -1 : undefined}
          className={cn(
            'ceciluz-wa-btn',
            // `group/wa` nombrado: permite que el auricular del logo SVG
            // reaccione al hover del BOTÓN (no del wrapper que también incluye
            // el tooltip), vía `group-hover/wa:fill-gold-500`.
            'group/wa',
            // Forma y tamaño responsive (mobile 52px, desktop 60px).
            'relative inline-flex items-center justify-center',
            'h-[52px] w-[52px] md:h-[60px] md:w-[60px]',
            'rounded-pill',
            // Fondo: gradiente violeta noche → vivido (sin verde).
            'bg-gradient-to-br from-primary-900 via-primary-800 to-primary-vivid',
            // Borde dorado fino del contenedor (la línea circular de
            // alrededor). El cambio crema↔dorado del usuario vive en el
            // CONTORNO del SVG, no aquí — ver `bubbleClassName` más abajo.
            'border border-gold-500/60',
            // Color base (los fills del logo SVG ya no dependen de currentColor;
            // se mantiene para cualquier descendiente que sí lo herede).
            'text-cream-50',
            // Transiciones (la animación de respiración la maneja la clase
            // .ceciluz-wa-btn; aquí sólo hover/active microinteracciones).
            'transition-[transform,box-shadow] duration-2 ease-out',
            // Hover: elevación.
            'hover:-translate-y-0.5',
            // Active: leve compresión.
            'active:scale-[0.97]',
            // Focus visible — anillo dorado del DS.
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-cream-100',
          )}
        >
          {/* Aro exterior dorado muy suave (decorativo, no recibe clicks). */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -inset-1 rounded-pill border border-gold-500/25"
          />

          {/* Halo interior con baja opacidad para profundidad. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-1 rounded-pill border border-gold-500/15"
          />

          {/* Ícono central — logo WhatsApp (burbuja + auricular) importado
              desde `../icons/WhatsAppLogo`. Dos regiones de color editables
              vía Tailwind:
                - bubble (verde original):
                    · fill   → SIEMPRE `fill-primary-700` (morado)
                    · stroke → CONTORNO crema en reposo, dorado en hover
                      (`stroke-cream-50` → `group-hover/wa:stroke-gold-500`).
                      `stroke-2` = 2 px en viewBox 32 (~6 % del ícono).
                - phone (blanco/negro original):
                    · fill → `fill-cream-50` (blanco) por defecto, dorado en
                      hover (`group-hover/wa:fill-gold-500`).
              Las transiciones de color suaves para que el cambio se sienta. */}
          <WhatsAppLogo
            className="relative h-7 w-7 md:h-8 md:w-8"
            bubbleClassName="fill-primary-700 stroke-cream-50 stroke-2 transition-colors duration-2 ease-out group-hover/wa:stroke-gold-500"
            phoneClassName="fill-cream-50 transition-colors duration-2 ease-out group-hover/wa:fill-gold-500"
          />

          {/* Destello dorado ✦ esquina superior derecha. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -right-0.5 -top-0.5 select-none text-[12px] leading-none text-gold-500 drop-shadow-[0_0_4px_rgba(212,175,55,0.6)]"
          >
            ✦
          </span>

          {/* Estrella pequeña ✧ esquina inferior izquierda. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-0 -left-0 select-none text-[10px] leading-none text-gold-soft opacity-80"
          >
            ✧
          </span>

          {/* Luna creciente decorativa pequeña (SVG inline currentColor). */}
          <svg
            aria-hidden="true"
            focusable={false}
            viewBox="0 0 24 24"
            className="pointer-events-none absolute -top-1 left-2 h-3 w-3 text-gold-soft opacity-80"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M16 4 a10 10 0 1 0 4 14 8 8 0 0 1 -4 -14 z" />
          </svg>
        </a>
      </div>
    </>
  );
}
